import { faBuilding, faCalendar, faDollarSign, faFile } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatCurrencyAmount, useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { Badge } from '@shared/components/ui/badge';
import { SectionBox } from '@shared/components/ui/SectionBox';
import { useTranslation } from 'react-i18next';
import { CourierFileDetail } from '../../models';

interface CourierFileHeaderProps {
  courierFile: CourierFileDetail;
  isFetching?: boolean;
}

// Función utilitaria para formatear fechas como dd/mm/yyyy
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  } catch {
    return '-';
  }
};

export const CourierFileHeader = ({ courierFile, isFetching = false }: CourierFileHeaderProps) => {
  const { t } = useTranslation('codCourierFileDetail');
  const currency = useCurrency();

  // Función helper para normalizar el estado
  const normalizeStatus = (status: string | undefined): string => {
    if (!status) return '';
    return status.toUpperCase();
  };

  const normalizedStatus = normalizeStatus(courierFile.status);

  const statusVariants: Record<string, { variant: any; label: string }> = {
    PENDING: {
      variant: 'warning',
      label: t('header.status.pending', 'Pendiente'),
    },
    PROCESSED: {
      variant: 'success',
      label: t('header.status.processed', 'Procesado'),
    },
    IMPORTED: {
      variant: 'info',
      label: t('header.status.imported', 'Importado'),
    },
  };

  const statusInfo = statusVariants[normalizedStatus] || { variant: 'default', label: courierFile.status };

  const cards = [
    {
      title: t('header.courier', 'Courier'),
      value: courierFile.courier.name,
      icon: faBuilding,
      iconBgClass: 'bg-card-border',
      iconTextClass: 'text-icons',
    },
    {
      title: t('header.file_date', 'Fecha Archivo'),
      value: formatDate(courierFile.file_date),
      icon: faCalendar,
      iconBgClass: 'bg-card-border',
      iconTextClass: 'text-icons',
    },
    {
      title: t('header.total_declared', 'Total Declarado'),
      value: formatCurrencyAmount(courierFile.total_declared, currency),
      icon: faDollarSign,
      iconBgClass: 'bg-card-border',
      iconTextClass: 'text-icons',
    },
    {
      title: t('header.status_label', 'Estado'),
      value: <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>,
      icon: faFile,
      iconBgClass: 'bg-card-border',
      iconTextClass: 'text-icons',
    },
  ];

  return (
    <div className="mb-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {cards.map((card, index) => (
          <SectionBox key={index} className="">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="text-sm text-subtext mb-1">{card.title}</h3>
                <p className="text-md text-title">
                  {isFetching ? (
                    <span className="inline-flex items-center gap-2">
                      <span className="animate-pulse">...</span>
                    </span>
                  ) : (
                    card.value
                  )}
                </p>
              </div>
              <div className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0`}>
                <FontAwesomeIcon icon={card.icon} className={`w-4 h-4 ${card.iconTextClass}`} />
              </div>
            </div>
          </SectionBox>
        ))}
      </div>
    </div>
  );
};
